import {
  getDefaultThemeModeId,
  isThemeModeId
} from './theme_modes.js';
import { resolveThemeRef } from './runtime/ui-resolution.js';

export const PLATFORM_THEME_STORAGE_KEY = 'kg.ui.theme-mode.v1';

const DEFAULT_PLATFORM_SCOPE_ID = 'full-app';
const PLATFORM_SCOPE_IDS = Object.freeze([
  'all-theme-enabled-plugins',
  'full-app'
]);

function safeLocalStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
}

function normalizeThemeModeId(themeId) {
  const normalizedInput = isThemeModeId(themeId)
    ? String(themeId).trim().toLowerCase()
    : getDefaultThemeModeId();
  const resolution = resolveThemeRef({ themeId: normalizedInput });
  return resolution && resolution.themeRef && resolution.themeRef.themeId
    ? resolution.themeRef.themeId
    : getDefaultThemeModeId();
}

export function isPlatformThemeScopeId(scopeId) {
  return PLATFORM_SCOPE_IDS.includes(String(scopeId || '').trim());
}

export function getDefaultPlatformThemeScopeId() {
  return DEFAULT_PLATFORM_SCOPE_ID;
}

export function createDefaultPlatformThemeModeState(scopeId = DEFAULT_PLATFORM_SCOPE_ID) {
  return {
    version: 1,
    themeId: getDefaultThemeModeId(),
    requestedThemeId: getDefaultThemeModeId(),
    clamped: false,
    scopeId: isPlatformThemeScopeId(scopeId) ? String(scopeId).trim() : DEFAULT_PLATFORM_SCOPE_ID
  };
}

export function normalizePlatformThemeModeState(value, options = {}) {
  const fallback = createDefaultPlatformThemeModeState(options.scopeId);
  const normalizeThemeSelection = (requestedThemeId) => {
    const resolution = resolveThemeRef({ themeId: requestedThemeId });
    return {
      themeId: resolution?.themeRef?.themeId || getDefaultThemeModeId(),
      requestedThemeId: String(requestedThemeId || '').trim().toLowerCase() || getDefaultThemeModeId(),
      clamped: Boolean(resolution && resolution.clamped)
    };
  };
  if (typeof value === 'string') {
    const themeSelection = normalizeThemeSelection(value);
    return {
      version: 1,
      themeId: themeSelection.themeId,
      requestedThemeId: themeSelection.requestedThemeId,
      clamped: themeSelection.clamped,
      scopeId: fallback.scopeId
    };
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return fallback;
  }
  if (value.version != null && Number(value.version) !== 1) {
    return fallback;
  }
  const themeSelection = normalizeThemeSelection(value.themeId);
  return {
    version: 1,
    themeId: themeSelection.themeId,
    requestedThemeId: themeSelection.requestedThemeId,
    clamped: themeSelection.clamped,
    scopeId: isPlatformThemeScopeId(value.scopeId)
      ? String(value.scopeId).trim()
      : fallback.scopeId
  };
}

export function hasPersistedPlatformThemeMode() {
  const storage = safeLocalStorage();
  if (!storage) return false;
  try {
    return storage.getItem(PLATFORM_THEME_STORAGE_KEY) != null;
  } catch {
    return false;
  }
}

export function getPersistedPlatformThemeMode(options = {}) {
  const storage = safeLocalStorage();
  const fallback = createDefaultPlatformThemeModeState(options.scopeId);
  if (!storage) return fallback;

  let raw = null;
  try {
    raw = storage.getItem(PLATFORM_THEME_STORAGE_KEY);
  } catch {
    raw = null;
  }
  if (raw == null) return fallback;

  let normalized = fallback;
  let shouldRewrite = false;

  try {
    const text = String(raw).trim();
    if (!text) {
      shouldRewrite = true;
    } else if (text.startsWith('{')) {
      normalized = normalizePlatformThemeModeState(JSON.parse(text), options);
      shouldRewrite = true;
    } else {
      normalized = normalizePlatformThemeModeState(text, options);
      shouldRewrite = true;
    }
  } catch {
    normalized = fallback;
    shouldRewrite = true;
  }

  if (shouldRewrite) {
    try {
      storage.setItem(PLATFORM_THEME_STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // Ignore storage write failures; callers still receive the normalized state.
    }
  }

  return normalized;
}

export function setPersistedPlatformThemeMode(themeState = createDefaultPlatformThemeModeState()) {
  const normalizedState = normalizePlatformThemeModeState(themeState);
  const storage = safeLocalStorage();
  if (!storage) return normalizedState;

  try {
    storage.setItem(PLATFORM_THEME_STORAGE_KEY, JSON.stringify(normalizedState));
  } catch {
    // Ignore storage write failures and return the normalized state.
  }

  return normalizedState;
}

function assertPlatformThemeScopeStateContract() {
  if (getDefaultPlatformThemeScopeId() !== 'full-app') {
    throw new Error('The platform theme default scope id must remain "full-app".');
  }
  if (isPlatformThemeScopeId('settings-only')) {
    throw new Error('The platform theme storage key must not accept settings-only scope.');
  }
  const normalized = normalizePlatformThemeModeState({
    version: 99,
    themeId: 'unknown',
    scopeId: 'settings-only'
  });
  if (normalized.scopeId !== 'full-app') {
    throw new Error('Invalid platform theme scope ids must fall back to full-app.');
  }
  if (normalized.themeId !== getDefaultThemeModeId()) {
    throw new Error('Invalid platform theme ids must fall back to the default theme mode.');
  }
}

assertPlatformThemeScopeStateContract();
